import { useState } from "react";
import { AlertTriangle, Check, X, Clock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DriverApprovalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packageId: string | null;
  packageInfo?: {
    retailer: string;
    size: string;
    userName?: string;
  };
  onSuccess: () => void;
}

export const DriverApprovalDialog = ({
  open,
  onOpenChange,
  packageId,
  packageInfo,
  onSuccess,
}: DriverApprovalDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");

  const handleApprove = async () => {
    if (!packageId) return;
    
    setLoading(true);
    try {
      const { error } = await supabase
        .from("packages")
        .update({
          driver_approved: true,
          driver_approval_note: note || null,
          requires_driver_approval: false,
        })
        .eq("id", packageId);

      if (error) throw error;

      toast.success("Package approved for pickup");
      onSuccess();
      handleClose();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error approving package:", error);
      }
      toast.error("Failed to approve package");
    } finally {
      setLoading(false);
    }
  };

  const handleDeny = async () => {
    if (!packageId) return;
    
    setLoading(true);
    try {
      // Deny = queue for next pickup (remove from current pickup)
      const { error } = await supabase
        .from("packages")
        .update({
          driver_approved: false,
          driver_approval_note: note || "Queued for next available pickup",
          requires_driver_approval: false,
          pickup_id: null, // Remove from current pickup
          status: "pending", // Reset to pending so it can be scheduled again
        })
        .eq("id", packageId);

      if (error) throw error;

      toast.success("Package queued for next available pickup");
      onSuccess();
      handleClose();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error denying package:", error);
      }
      toast.error("Failed to queue package");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNote("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Late Package Submission
          </DialogTitle>
          <DialogDescription>
            This package was submitted within 8 hours of the scheduled pickup. 
            Driver approval is required.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {packageInfo && (
            <div className="bg-muted p-3 rounded-lg space-y-1">
              <p className="text-sm">
                <span className="font-medium">Retailer:</span> {packageInfo.retailer}
              </p>
              <p className="text-sm">
                <span className="font-medium">Size:</span> {packageInfo.size}
              </p>
              {packageInfo.userName && (
                <p className="text-sm">
                  <span className="font-medium">Customer:</span> {packageInfo.userName}
                </p>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
            <Clock className="h-4 w-4 text-amber-600" />
            <span className="text-sm text-amber-800 dark:text-amber-200">
              Submitted within 8-hour cutoff window
            </span>
          </div>

          <div className="space-y-2">
            <Label htmlFor="note">Note (optional)</Label>
            <Textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note about this decision..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter className="flex gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={handleDeny}
            disabled={loading}
            className="flex-1"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <X className="h-4 w-4 mr-2" />
                Queue for Next
              </>
            )}
          </Button>
          <Button
            onClick={handleApprove}
            disabled={loading}
            className="flex-1"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Approve Pickup
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
